import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { KlachtError, updateKlachtStatus } from '../../lib/klachten';
import { KLACHT_STATUSES } from '../../lib/klacht-constants';
import { useKlachtDialogStore } from '../../stores/klacht-dialog-store';

interface Props {
  onChanged?: () => void;
}

/**
 * Dialog voor status-wijziging. `payload.newStatus` mag pre-set worden
 * vanuit detail-page-knoppen (bv. "Wacht op klant" → 'Wacht_op_Klant').
 * Als `newStatus` niet pre-set is, kan office vrij kiezen.
 */
export function KlachtStatusDialog({ onChanged }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'status';
  const klachtId = payload.klachtId;
  const presetStatus = payload.newStatus;

  const [newStatus, setNewStatus] = useState<string>(presetStatus ?? 'In_Behandeling');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNewStatus(presetStatus ?? 'In_Behandeling');
      setNote('');
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen, presetStatus]);

  const handleSubmit = useCallback(async () => {
    if (!klachtId) {
      setError('Geen klacht-id');
      return;
    }
    if (!newStatus) {
      setError('Kies een status');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateKlachtStatus({ klachtId, newStatus, note: note.trim() || undefined });
      close();
      onChanged?.();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Status-wijziging mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [klachtId, newStatus, note, close, onChanged]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Status wijzigen</h2>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">Nieuwe status</span>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              disabled={submitting}
            >
              {KLACHT_STATUSES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">Toelichting</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Optionele context bij de status-wijziging"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </label>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !newStatus}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !newStatus
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-slate-700 text-white hover:bg-slate-800'
            )}
          >
            {submitting ? 'Bezig…' : 'Status wijzigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
